import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'

import type { ShoppingListItem, SupplierOption } from '@/api/shoppingList'
import { shoppingListApi } from '@/api/shoppingList'
import { AppShell } from '@/components/AppShell'
import { Sheet } from '@/components/Sheet'
import { toast } from '@/lib/toast'
import { IconCart, IconCheck, IconPlus, IconSearch, IconStar, IconStore } from '@/components/icons'
import { Alert, Button, EmptyState, SkeletonList } from '@/components/ui'
import { useApiError } from '@/features/auth/useApiError'
import { FavoriteChips, QuantityStepper } from '@/features/list/quantity'
import { ProductForm, SupplierAdder } from '@/features/products/ProductForm'
import { useAuthStore } from '@/stores/authStore'
import { formatDate } from '@/lib/format'

/**
 * Η κύρια αφετηρία παραγγελίας.
 *
 * Ο εστιάτορας δεν σκέφτεται ανά προμηθευτή· σκέφτεται τι του λείπει. Εδώ
 * περνά ποσότητες σε μία ενιαία λίστα και το σπάσιμο σε παραγγελίες γίνεται
 * μόνο του. Καμία πληκτρολόγηση στην κανονική διαδρομή: chips για τα
 * συνηθισμένα, συν και πλην για τα υπόλοιπα.
 */
export default function ShoppingListPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const translateError = useApiError()

  const [search, setSearch] = useState('')
  /** Ποσότητα ανά κλειδί είδους. Κενό σημαίνει «δεν το θέλω τώρα». */
  const [quantities, setQuantities] = useState<Record<string, string>>({})
  /** Ρητή επιλογή προμηθευτή, όπου ο χρήστης δεν δέχτηκε την πρόταση. */
  const [chosen, setChosen] = useState<Record<string, string>>({})
  const [picking, setPicking] = useState<string | null>(null)
  /** Γρήγορη καταχώριση χωρίς να φύγει ο χρήστης από τη λίστα του. */
  const [sheet, setSheet] = useState<
    { kind: 'product' } | { kind: 'supplier'; key: string; name: string } | null
  >(null)
  const canManage = useAuthStore((s) => s.hasPermission('item:manage'))

  const list = useQuery({
    queryKey: ['shopping-list', search],
    queryFn: () => shoppingListApi.get({ search: search || undefined }),
  })

  const optionFor = (item: ShoppingListItem): SupplierOption => {
    const explicit = chosen[item.key]
    return item.options.find((o) => o.supplierId === explicit) ?? item.options[0]
  }

  const setQuantity = (key: string, value: string) =>
    setQuantities((current) => ({ ...current, [key]: value }))

  const { lines, supplierCount } = useMemo(() => {
    const items = list.data?.items ?? []
    const collected = items
      .filter((item) => Number(quantities[item.key] ?? '') > 0)
      .map((item) => {
        const option = optionFor(item)
        return {
          supplierId: option.supplierId,
          businessItemId: option.businessItemId,
          quantity: quantities[item.key],
        }
      })
    return {
      lines: collected,
      supplierCount: new Set(collected.map((line) => line.supplierId)).size,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data, quantities, chosen])

  const submit = useMutation({
    mutationFn: () => shoppingListApi.submit(lines),
    onSuccess: () => {
      setQuantities({})
      void queryClient.invalidateQueries({ queryKey: ['cart'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      toast.success(t('list.added'))
      navigate('/cart')
    },
  })

  // Τα chips δείχνουν πρώτα τα σημαδεμένα αγαπημένα και συμπληρώνονται με
  // ό,τι παραγγέλνεται πιο συχνά. Χωρίς αυτό η γραμμή θα ήταν κενή μέχρι να
  // μπει κάποιος να σημάνει αγαπημένα με το χέρι, δηλαδή πρακτικά ποτέ.
  const usual = useMemo(() => {
    const items = list.data?.items ?? []
    const marked = items.filter((item) => item.isFavorite)
    const frequent = items
      .filter((item) => !item.isFavorite && item.orderCount > 0)
      .sort((a, b) => b.orderCount - a.orderCount)
    return [...marked, ...frequent].slice(0, 10)
  }, [list.data])
  const isEmpty = list.data?.items.length === 0 && !search

  // Η λίστα χωρίζεται όπως είναι στημένη η αποθήκη: «Καφέδες», «Γάλατα»… με
  // τη σειρά που όρισε ο χρήστης, και ό,τι δεν έχει κατηγορία στο τέλος.
  const sections = useMemo(() => {
    const items = list.data?.items ?? []
    const categories = list.data?.categories ?? []
    const known = new Set(categories.map((category) => category.id))
    const result = categories.map((category) => ({
      id: category.id,
      name: category.name,
      items: items.filter((item) => item.categoryId === category.id),
    }))
    result.push({
      id: 'none',
      name: t('list.uncategorized'),
      items: items.filter((item) => !item.categoryId || !known.has(item.categoryId)),
    })
    return result.filter((section) => section.items.length > 0)
  }, [list.data, t])
  // Χωρίς καμία κατηγορία η επικεφαλίδα «Χωρίς κατηγορία» είναι θόρυβος.
  const showHeadings = (list.data?.categories.length ?? 0) > 0

  return (
    <AppShell title={t('list.title')}>
      {canManage && list.data && !isEmpty && (
        <div className="mb-3 flex items-center gap-2">
          <button
            onClick={() => setSheet({ kind: 'product' })}
            className="flex h-10 items-center gap-1.5 rounded-xl bg-brand-50 px-3 text-sm font-semibold text-brand-800 hover:bg-brand-100"
          >
            <IconPlus size={16} />
            {t('list.addProduct')}
          </button>
          <Link
            to="/products"
            className="flex h-10 items-center rounded-xl px-3 text-sm font-semibold text-gray-600 hover:bg-gray-100"
          >
            {t('list.manage')}
          </Link>
        </div>
      )}

      {!isEmpty && list.data && (
        <>
          <FavoriteChips items={usual} quantities={quantities} onBump={setQuantity} />

          <div className="relative mb-3">
            <IconSearch
              size={18}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={t('list.searchPlaceholder')}
              className="h-touch w-full rounded-xl border border-gray-300 bg-white pl-10 pr-3 text-base text-gray-900 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-100"
            />
          </div>
        </>
      )}

      {list.isPending && <SkeletonList rows={6} />}
      {list.isError && <Alert>{translateError(list.error)}</Alert>}
      {submit.isError && (
        <div className="mb-3">
          <Alert>{translateError(submit.error)}</Alert>
        </div>
      )}

      {isEmpty && (
        <EmptyState
          icon={<IconStore size={26} />}
          title={t('list.empty')}
          description={t('list.emptyHint')}
          action={
            canManage ? (
              <Button onClick={() => setSheet({ kind: 'product' })}>
                <IconPlus size={19} />
                {t('list.emptyAction')}
              </Button>
            ) : undefined
          }
        />
      )}

      {list.data && list.data.items.length === 0 && !isEmpty && (
        <p className="rounded-2xl border border-gray-200/80 bg-white p-6 text-center text-sm text-gray-500 shadow-card">
          {t('list.noResults')}
        </p>
      )}

      <div className="pb-24">
        {sections.map((section) => (
          <section key={section.id} className="mb-4">
            {showHeadings && (
              <h2 className="mb-2 px-1 text-xs font-bold uppercase tracking-widest text-gray-500">
                {section.name}
              </h2>
            )}
            <ul className="flex flex-col gap-2">
              {section.items.map((item) => {
                const option = optionFor(item)
                const quantity = quantities[item.key] ?? ''
                const active = Number(quantity) > 0

                return (
                  <li key={item.key}>
                    <div
                      className={
                        'rounded-2xl border bg-white shadow-card transition-colors ' +
                        (active ? 'border-brand-400 ring-1 ring-brand-200' : 'border-gray-200/80')
                      }
                    >
                      <div className="flex items-center gap-3 p-3">
                        <div className="min-w-0 flex-1">
                          <p className="flex items-center gap-1.5 truncate font-semibold text-gray-900">
                            {item.isFavorite && (
                              <IconStar size={15} filled className="shrink-0 text-amber-500" />
                            )}
                            {item.name}
                          </p>
                          {item.note && (
                            <p className="truncate text-xs italic text-gray-500">{item.note}</p>
                          )}

                          {/* Ο προμηθευτής είναι κουμπί όταν υπάρχει εναλλακτική:
                        η επιλογή γίνεται εδώ, χωρίς να χαθεί η θέση στη λίστα. */}
                          {item.options.length > 1 || canManage ? (
                            <button
                              onClick={() => setPicking(picking === item.key ? null : item.key)}
                              className={
                                'mt-0.5 flex max-w-full items-center gap-1 truncate rounded-lg px-2 py-0.5 text-xs font-medium ' +
                                (item.options.length > 1
                                  ? 'bg-brand-50 text-brand-700 hover:bg-brand-100'
                                  : 'text-gray-500 hover:bg-gray-100')
                              }
                            >
                              {option.supplierName}
                              <span className="text-[10px]">▾</span>
                            </button>
                          ) : (
                            <p className="mt-0.5 truncate text-xs text-gray-500">
                              {option.supplierName}
                            </p>
                          )}

                          {!option.isAvailableToday && (
                            <p className="mt-0.5 text-xs font-medium text-amber-700">
                              {option.unavailableUntil
                                ? t('availability.until', {
                                    date: formatDate(option.unavailableUntil),
                                  })
                                : t('list.unavailable')}
                            </p>
                          )}
                        </div>

                        {!active && (
                          <span className="w-9 shrink-0 text-right text-xs text-gray-400">
                            {t(`units.${item.unit}`)}
                          </span>
                        )}
                        <QuantityStepper
                          value={quantity}
                          unit={item.unit}
                          onChange={(value) => setQuantity(item.key, value)}
                        />
                      </div>

                      {picking === item.key && (
                        <ul className="border-t border-gray-100 p-2">
                          {item.options.map((candidate) => {
                            const selected = candidate.supplierId === option.supplierId
                            return (
                              <li key={candidate.supplierId}>
                                <button
                                  onClick={() => {
                                    setChosen((current) => ({
                                      ...current,
                                      [item.key]: candidate.supplierId,
                                    }))
                                    setPicking(null)
                                  }}
                                  className={
                                    'flex w-full items-center gap-2 rounded-xl px-3 py-3 text-left text-sm transition-colors ' +
                                    (selected
                                      ? 'bg-brand-50 font-semibold text-brand-900'
                                      : 'hover:bg-gray-50')
                                  }
                                >
                                  <span className="min-w-0 flex-1 truncate">
                                    {candidate.supplierName}
                                    {!candidate.isAvailableToday && (
                                      <span className="ml-2 text-xs font-normal text-amber-700">
                                        {t('list.unavailable')}
                                      </span>
                                    )}
                                  </span>
                                  {candidate.price && (
                                    <span className="shrink-0 tabular-nums text-gray-600">
                                      {Number(candidate.price).toFixed(2)} €
                                    </span>
                                  )}
                                  {selected && <IconCheck size={16} className="shrink-0" />}
                                </button>
                              </li>
                            )
                          })}
                          {canManage && (
                            <li>
                              <button
                                onClick={() => {
                                  setPicking(null)
                                  setSheet({ kind: 'supplier', key: item.key, name: item.name })
                                }}
                                className="flex w-full items-center rounded-xl px-3 py-3 text-left text-sm font-semibold text-brand-700 hover:bg-brand-50"
                              >
                                {t('list.otherSupplier')}
                              </button>
                            </li>
                          )}
                        </ul>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          </section>
        ))}
      </div>

      {sheet?.kind === 'product' && (
        <Sheet title={t('products.add')} onClose={() => setSheet(null)}>
          <ProductForm onDone={() => setSheet(null)} />
        </Sheet>
      )}
      {sheet?.kind === 'supplier' && (
        <Sheet
          title={t('products.addSupplierTitle', { name: sheet.name })}
          onClose={() => setSheet(null)}
        >
          <SupplierAdder
            productKey={sheet.key}
            onAdded={(supplierId) => {
              // Ο νέος προμηθευτής γίνεται αμέσως η επιλογή για αυτό το είδος.
              setChosen((current) => ({ ...current, [sheet.key]: supplierId }))
              setSheet(null)
            }}
          />
        </Sheet>
      )}

      {/* Η μπάρα αποστολής κάθεται πάνω από την πλοήγηση: ο χρήστης βλέπει
          διαρκώς πόσα διάλεξε και σε πόσους θα φύγουν. */}
      {lines.length > 0 && (
        <div className="fixed inset-x-0 bottom-[68px] z-10 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur md:bottom-0 md:left-[var(--sidebar-w,0px)]">
          <div className="mx-auto w-full max-w-3xl">
            <div className="mb-2 flex items-center justify-between text-xs text-gray-500">
              <span>{t('list.willSplit', { count: supplierCount })}</span>
              <button
                onClick={() => setQuantities({})}
                className="font-semibold text-gray-500 hover:text-gray-700"
              >
                {t('list.clear')}
              </button>
            </div>
            <Button loading={submit.isPending} onClick={() => submit.mutate()}>
              <IconCart size={19} />
              {t('list.selected', { count: lines.length })}
            </Button>
          </div>
        </div>
      )}
    </AppShell>
  )
}
